import { useCallback, useEffect, useState } from 'react';
import {
  createCompetenceRecord,
  fetchCompetenceGaps,
  fetchCompetenceRecords,
  fetchJobRoles,
  updateCompetenceRecord,
} from './competenceApi';
import { formatCompetencies } from './competenceUtils';
import type { CompetenceRecord, CompetencyItem, GapSummary, JobRole } from './types';

interface CompetenceRecordsTabProps {
  canEdit: boolean;
}

const emptySkill = (): CompetencyItem => ({ name: '', level: 1 });

export function CompetenceRecordsTab({ canEdit }: CompetenceRecordsTabProps) {
  const [records, setRecords] = useState<CompetenceRecord[]>([]);
  const [gaps, setGaps] = useState<GapSummary[]>([]);
  const [jobRoles, setJobRoles] = useState<JobRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGaps, setShowGaps] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CompetenceRecord | null>(null);
  const [personName, setPersonName] = useState('');
  const [jobRoleId, setJobRoleId] = useState('');
  const [skills, setSkills] = useState<CompetencyItem[]>([emptySkill()]);
  const [gapNotes, setGapNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [recordsData, rolesData, gapsData] = await Promise.all([
        fetchCompetenceRecords(),
        fetchJobRoles({ active: true }),
        fetchCompetenceGaps(),
      ]);
      setRecords(recordsData);
      setJobRoles(rolesData);
      setGaps(gapsData);
    } catch {
      setError('No se pudieron cargar los registros de competencia.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const personLabel = (record: CompetenceRecord) =>
    record.externalPersonName ?? (record.userId ? `Usuario #${record.userId}` : '—');

  const openCreate = () => {
    setFormOpen(true);
    setEditing(null);
    setPersonName('');
    setJobRoleId('');
    setSkills([emptySkill()]);
    setGapNotes('');
  };

  const openEdit = (record: CompetenceRecord) => {
    setFormOpen(true);
    setEditing(record);
    setPersonName(record.externalPersonName ?? '');
    setJobRoleId(record.jobRoleId ?? '');
    setSkills(record.skills?.length ? [...record.skills] : [emptySkill()]);
    setGapNotes(record.gapNotes ?? '');
  };

  const handleSave = async () => {
    const skillItems = skills.filter((s) => s.name.trim());
    if (!personName.trim()) return;
    setSaving(true);
    try {
      const payload = {
        externalPersonName: personName.trim(),
        jobRoleId: jobRoleId || null,
        skills: skillItems,
        gapNotes: gapNotes.trim() || null,
        evaluatedAt: new Date().toISOString(),
      };
      if (editing) {
        await updateCompetenceRecord(editing.id, payload);
      } else {
        await createCompetenceRecord(payload);
      }
      setFormOpen(false);
      setEditing(null);
      setPersonName('');
      setJobRoleId('');
      setSkills([emptySkill()]);
      setGapNotes('');
      await load();
    } catch {
      setError('No se pudo guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={showGaps} onChange={(e) => setShowGaps(e.target.checked)} />
          Mostrar solo brechas detectadas ({gaps.length})
        </label>
        {canEdit && (
          <button
            type="button"
            className="text-sm rounded bg-primary text-primary-foreground px-3 py-1.5"
            onClick={openCreate}
          >
            Nuevo registro
          </button>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : showGaps ? (
        <div className="space-y-2">
          {gaps.map((entry) => (
            <div key={entry.recordId} className="border rounded-md p-3 text-sm">
              <p className="font-medium">
                {entry.externalPersonName ?? (entry.userId ? `Usuario #${entry.userId}` : '—')}
                {entry.jobRoleName ? ` · ${entry.jobRoleName}` : ''}
              </p>
              <ul className="mt-1 list-disc pl-4 text-muted-foreground">
                {entry.gaps.map((gap) => (
                  <li key={gap.name}>
                    {gap.name}: requiere nivel {gap.requiredLevel}
                    {gap.type === 'missing'
                      ? ' (no registrada)'
                      : ` (tiene ${gap.actualLevel})`}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {gaps.length === 0 && (
            <p className="text-sm text-muted-foreground">No hay brechas de competencia detectadas.</p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-md">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2">Persona</th>
                <th className="text-left p-2">Puesto</th>
                <th className="text-left p-2">Competencias</th>
                <th className="text-left p-2">Evaluado</th>
                {canEdit && <th className="text-right p-2">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id} className="border-t border-border">
                  <td className="p-2">{personLabel(record)}</td>
                  <td className="p-2">{record.jobRole?.name ?? '—'}</td>
                  <td className="p-2 text-muted-foreground">{formatCompetencies(record.skills)}</td>
                  <td className="p-2">
                    {record.evaluatedAt
                      ? new Date(record.evaluatedAt).toLocaleDateString()
                      : '—'}
                  </td>
                  {canEdit && (
                    <td className="p-2 text-right">
                      <button
                        type="button"
                        className="text-primary text-xs"
                        onClick={() => openEdit(record)}
                      >
                        Editar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {records.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 5 : 4} className="p-4 text-center text-muted-foreground">
                    Sin registros de competencia.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {canEdit && formOpen && (
        <div className="border rounded-md p-4 space-y-3 bg-muted/20">
          <h3 className="font-medium">{editing ? 'Editar registro' : 'Nuevo registro'}</h3>
          <input
            className="w-full border rounded px-2 py-1.5 text-sm bg-background"
            placeholder="Nombre de la persona (externa o referencia)"
            value={personName}
            onChange={(e) => setPersonName(e.target.value)}
          />
          <select
            className="w-full border rounded px-2 py-1.5 text-sm bg-background"
            value={jobRoleId}
            onChange={(e) => setJobRoleId(e.target.value)}
          >
            <option value="">Sin puesto asignado</option>
            {jobRoles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Competencias actuales</p>
            {skills.map((item, idx) => (
              <div key={idx} className="flex gap-2">
                <input
                  className="flex-1 border rounded px-2 py-1 text-sm bg-background"
                  placeholder="Competencia"
                  value={item.name}
                  onChange={(e) => {
                    const next = [...skills];
                    next[idx] = { ...next[idx], name: e.target.value };
                    setSkills(next);
                  }}
                />
                <input
                  type="number"
                  min={0}
                  max={10}
                  className="w-20 border rounded px-2 py-1 text-sm bg-background"
                  value={item.level}
                  placeholder="Nivel"
                  onChange={(e) => {
                    const next = [...skills];
                    next[idx] = { ...next[idx], level: Number(e.target.value) };
                    setSkills(next);
                  }}
                />
              </div>
            ))}
            <button
              type="button"
              className="text-xs text-primary"
              onClick={() => setSkills([...skills, emptySkill()])}
            >
              + Agregar competencia
            </button>
          </div>
          <textarea
            className="w-full border rounded px-2 py-1.5 text-sm bg-background"
            placeholder="Notas sobre brechas"
            rows={2}
            value={gapNotes}
            onChange={(e) => setGapNotes(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={saving}
              className="text-sm rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
              onClick={() => void handleSave()}
            >
              Guardar
            </button>
            <button
              type="button"
              className="text-sm rounded border px-3 py-1.5"
              onClick={() => {
                setFormOpen(false);
                setEditing(null);
                setPersonName('');
                setJobRoleId('');
                setSkills([emptySkill()]);
                setGapNotes('');
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
